/**
 * mail_readMessage Command
 * 
 * Read a specific email message or thread
 * Similar to doc_read but for email content
 */

import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../../types/commands';
import useEntityStore from '../../../../stores/entityStore';
import type { MailThreadEntity, MailMessageEntity, MailDraftEntity } from '../../types/entities';
import { htmlToPlainText } from '../../utils/emailHelpers';
import { mailCRDTService } from '../../services/crdt/mailCRDTService';

export const mailReadMessageCommand: SimpleDocCommand = {
  id: 'mail_readMessage',
  logicalName: 'mail_readMessage',
  version: 'v1',
  
  description: `Read email message or thread content - returns formatted email with headers and body.

**SUPPORTED ENTITIES:**
- Mail.Message (single email)
- Mail.Thread (full conversation)
- Mail.Draft (work-in-progress email)

**BODY STORAGE:**
Drafts read from Y.js (Y.Text) for real-time collaboration, like code_write and note_write.
Messages and threads read from entity skeleton.

**EXAMPLES:**

Read thread:
{
  "id": "mail_thread_abc123"
}

Read message with headers:
{
  "id": "mail_message_xyz789",
  "format": "full"
}

Read draft:
{
  "id": "draft_abc",
  "format": "plain"
}`,
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Message, thread, or draft entity ID',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'ID is required';
          }
          return null;
        }
      },
      {
        name: 'format',
        type: 'string',
        description: 'Output format: "plain" (default), "html", or "full" (includes all headers)',
        required: false,
        enum: ['plain', 'html', 'full'],
        validator: (value: string) => {
          if (value && !['plain', 'html', 'full'].includes(value)) {
            return 'format must be "plain", "html", or "full"';
          }
          return null;
        }
      },
      {
        name: 'maxLength',
        type: 'number',
        description: 'Maximum characters to return (default: 10000)',
        required: false,
        validator: (value: number) => {
          if (value && (value < 100 || value > 100000)) {
            return 'maxLength must be between 100 and 100,000';
          }
          return null;
        }
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: { id: 'mail_thread_abc123' },
        expectedOutput: createSuccessResult(
          'Read thread: Team Meeting Notes (3 messages)',
          {
            type: 'thread',
            subject: 'Team Meeting Notes',
            messageCount: 3,
            content: '...',
          }
        ),
        description: 'Read full thread conversation',
      },
      {
        input: { id: 'mail_message_xyz789', format: 'plain' },
        expectedOutput: createSuccessResult(
          'Read message: Re: Project Update',
          {
            type: 'message',
            subject: 'Re: Project Update',
            from: 'john@example.com',
            content: '...',
          }
        ),
        description: 'Read single message as plain text',
      },
    ],
    bad: [
      {
        input: { id: '' },
        error: 'ID is required',
        why: 'Empty ID provided',
        fix: 'Provide valid message, thread, or draft ID'
      },
      {
        input: { id: 'invalid_id', format: 'invalid' },
        error: 'format must be "plain", "html", or "full"',
        why: 'Invalid format value',
        fix: 'Use one of: plain, html, full'
      }
    ],
  },
  
  formatViewData: (result: any, toolInput: any) => {
    if (result.status === 'success') {
      return {
        type: 'mail_read',
        summary: result.message,
        data: result.data
      };
    }
    return null;
  },
  
  handler: async (
    params: { id: string; format?: string; maxLength?: number },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const { id, format = 'plain', maxLength = 10000 } = params;
      
      if (!id) {
        return createErrorResult('Missing ID', 'Please provide a message or thread ID', 'validation');
      }
      
      console.log('[MailApp:ReadMessage] Reading entity:', id);
      
      // Get entity from store
      const entityState = useEntityStore.getState();
      const entity = entityState.entities.find(
        (e: any) => e._id === id || e.llmId === id || e.id === id
      );
      
      if (!entity) {
        return createErrorResult('Entity not found', `No entity found with ID: ${id}`, 'validation');
      }
      
      console.log('[MailApp:ReadMessage] Found entity:', entity.entityType);
      
      // Handle Mail.Thread
      if (entity.entityType === 'Mail.Thread') {
        return await readThread(entity as MailThreadEntity, entityState.entities, format, maxLength);
      }
      
      // Handle Mail.Message
      if (entity.entityType === 'Mail.Message') {
        return await readMessage(entity as MailMessageEntity, format, maxLength);
      }
      
      // Handle Mail.Draft - read from Y.Text (source of truth)
      if (entity.entityType === 'Mail.Draft') {
        return await readDraft(entity as MailDraftEntity, format, maxLength);
      }
      
      return createErrorResult(
        'Unsupported entity type',
        `This command only works with Mail.Thread, Mail.Message, and Mail.Draft entities. Got: ${entity.entityType}`,
        'validation'
      );
    } catch (error) {
      console.error('[MailApp:ReadMessage] Error:', error);
      return createErrorResult(
        'Read failed',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

// ==================== Helper Functions ====================

async function readThread(
  thread: MailThreadEntity,
  allEntities: any[],
  format: string,
  maxLength: number
): Promise<StandardToolResult> {
  const skeleton = thread.skeleton;
  
  // Find all messages in thread
  const messages = allEntities.filter(
    (e: any) => skeleton.messageIds.includes(e._id) && e.entityType === 'Mail.Message'
  ) as MailMessageEntity[];
  
  // Sort by sentAt ascending (oldest first)
  messages.sort((a, b) => {
    const aTime = new Date(a.skeleton.sentAt).getTime();
    const bTime = new Date(b.skeleton.sentAt).getTime();
    return aTime - bTime;
  });
  
  console.log('[MailApp:ReadMessage] Thread has messages:', messages.length);
  
  // Format thread content
  let content = `# Thread: ${thread.name}\n\n`;
  content += `**Participants:** ${skeleton.participants.map(p => p.email).join(', ')}\n`;
  content += `**Messages:** ${messages.length}\n`;
  content += `**Last message:** ${new Date(skeleton.lastMessageAt).toLocaleString()}\n\n`;
  content += `---\n\n`;
  
  // Add each message
  for (const msg of messages) {
    const msgContent = formatMessage(msg, format);
    content += msgContent + '\n\n---\n\n';
  }
  
  // Truncate if too long
  if (content.length > maxLength) {
    const truncated = content.substring(0, maxLength);
    content = truncated + `\n\n[... truncated after ${maxLength} characters. ${messages.length - 3} more messages in thread]`;
  }
  
  return createSuccessResult(
    `Read thread: ${thread.name} (${messages.length} message${messages.length > 1 ? 's' : ''})`,
    {
      type: 'thread',
      id: thread._id,
      subject: thread.name,
      messageCount: messages.length,
      participants: skeleton.participants,
      content,
      messages: messages.map(m => ({
        id: m._id,
        from: m.skeleton.from[0]?.email,
        subject: m.name,
        sentAt: m.skeleton.sentAt,
      })),
    }
  );
}

async function readMessage(
  message: MailMessageEntity,
  format: string,
  maxLength: number
): Promise<StandardToolResult> {
  console.log('[MailApp:ReadMessage] Reading single message:', message.name);
  
  const content = formatMessage(message, format);
  
  // Truncate if too long
  const finalContent = content.length > maxLength
    ? content.substring(0, maxLength) + `\n\n[... truncated after ${maxLength} characters]`
    : content;
  
  return createSuccessResult(
    `Read message: ${message.name}`,
    {
      type: 'message',
      id: message._id,
      subject: message.name,
      from: message.skeleton.from[0]?.email,
      to: message.skeleton.to.map(p => p.email),
      sentAt: message.skeleton.sentAt,
      content: finalContent,
    }
  );
}

function formatMessage(message: MailMessageEntity, format: string): string {
  const skeleton = message.skeleton;
  
  let output = '';
  
  // Full format includes headers
  if (format === 'full') {
    output += `**From:** ${skeleton.from.map(p => `${p.name || ''} <${p.email}>`).join(', ')}\n`;
    output += `**To:** ${skeleton.to.map(p => `${p.name || ''} <${p.email}>`).join(', ')}\n`;
    if (skeleton.cc && skeleton.cc.length > 0) {
      output += `**CC:** ${skeleton.cc.map(p => `${p.name || ''} <${p.email}>`).join(', ')}\n`;
    }
    output += `**Subject:** ${message.name}\n`;
    output += `**Date:** ${new Date(skeleton.sentAt).toLocaleString()}\n\n`;
  } else {
    // Simple format
    output += `**From:** ${skeleton.from[0]?.email || 'Unknown'}\n`;
    output += `**Subject:** ${message.name}\n`;
    output += `**Date:** ${new Date(skeleton.sentAt).toLocaleString()}\n\n`;
  }
  
  // Body content
  if (format === 'html' && skeleton.bodyHtml) {
    output += skeleton.bodyHtml;
  } else if (skeleton.bodyPlain) {
    output += skeleton.bodyPlain;
  } else if (skeleton.bodyHtml) {
    // Convert HTML to plain text
    output += htmlToPlainText(skeleton.bodyHtml);
  } else {
    output += '(No content)';
  }
  
  // Attachments
  if (skeleton.attachments && skeleton.attachments.length > 0) {
    output += '\n\n**Attachments:**\n';
    skeleton.attachments.forEach(att => {
      output += `- ${att.filename} (${att.contentType}, ${att.size} bytes)\n`;
    });
  }
  
  return output;
}

async function readDraft(
  draft: MailDraftEntity,
  format: string,
  maxLength: number
): Promise<StandardToolResult> {
  console.log('[MailApp:ReadMessage] Reading draft:', draft.name);
  
  try {
    // Read body from Y.Text (source of truth)
    const { ydoc } = await mailCRDTService.getYDocForDraft(draft._id);
    const ytext = ydoc.getText('body');
    const bodyContent = ytext.toString();
    
    console.log('[MailApp:ReadMessage] Draft body from Y.Text', { 
      bodyLength: bodyContent.length 
    });
    
    let content = '';
    
    // Headers
    const to = draft.skeleton?.to || [];
    const cc = draft.skeleton?.cc || [];
    const bcc = draft.skeleton?.bcc || [];
    
    if (format === 'full') {
      content += `**To:** ${to.length > 0 ? to.map(p => `${p.name || ''} <${p.email}>`).join(', ') : 'Not set'}\n`;
      if (cc.length > 0) {
        content += `**CC:** ${cc.map(p => `${p.name || ''} <${p.email}>`).join(', ')}\n`;
      }
      if (bcc.length > 0) {
        content += `**BCC:** ${bcc.map(p => `${p.name || ''} <${p.email}>`).join(', ')}\n`;
      }
      content += `**Subject:** ${draft.name || 'Untitled'}\n\n`;
    } else {
      content += `**To:** ${to[0]?.email || 'Not set'}\n`;
      content += `**Subject:** ${draft.name || 'Untitled'}\n\n`;
    }
    
    // Body from Y.Text
    content += bodyContent || '(No content)';
    
    // Truncate if too long
    const finalContent = content.length > maxLength
      ? content.substring(0, maxLength) + `\n\n[... truncated after ${maxLength} characters]`
      : content;
    
    return createSuccessResult(
      `Read draft: ${draft.name || 'Untitled'}`,
      {
        type: 'draft',
        id: draft._id,
        subject: draft.name || 'Untitled',
        to: to.map(p => p.email),
        cc: cc.map(p => p.email),
        bcc: bcc.map(p => p.email),
        content: finalContent,
        bodyLength: bodyContent.length,
      }
    );
  } catch (error) {
    console.error('[MailApp:ReadMessage] Failed to read draft:', error);
    return createErrorResult(
      'Failed to read draft',
      error instanceof Error ? error.message : 'Unknown error',
      'execution'
    );
  }
}
